import React, { useState, useEffect } from 'react';
import { clientsAPI, salesAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import '../css/PaymentManagement.css'; // Reusing tab styles

const MajorClientReport = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('darling');
    const [branchData, setBranchData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const darlingBranchIds = [
        'd3c01167-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3bec7c0-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3bed8c9-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3bf1328-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3bf24bb-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3bfefcd-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3c006d0-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3cd94fb-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3bef8c6-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3c006d0-fa8d-11f0-8b0a-9c63c01b58e2',
        'd3cdb635-fa8d-11f0-8b0a-9c63c01b58e2'
    ];

    const tvcBranchIds = [
        '63d18ba1-17a3-11f1-8403-9c63c01b58e2',
        '63d1d812-17a3-11f1-8403-9c63c01b58e2',
        '63d2149c-17a3-11f1-8403-9c63c01b58e2',
        '63d250ac-17a3-11f1-8403-9c63c01b58e2',
        '63d29276-17a3-11f1-8403-9c63c01b58e2',
        '63d2d57b-17a3-11f1-8403-9c63c01b58e2',
        '63d30ee5-17a3-11f1-8403-9c63c01b58e2',
        '63d34d8d-17a3-11f1-8403-9c63c01b58e2',
        '63d38c67-17a3-11f1-8403-9c63c01b58e2',
        '63d3d6b1-17a3-11f1-8403-9c63c01b58e2',
        '63d41cc4-17a3-11f1-8403-9c63c01b58e2',
        '63d45e22-17a3-11f1-8403-9c63c01b58e2',
        '63d4a59c-17a3-11f1-8403-9c63c01b58e2',
        '63d4e5a6-17a3-11f1-8403-9c63c01b58e2',
        '63d5246e-17a3-11f1-8403-9c63c01b58e2',
        '63d562cc-17a3-11f1-8403-9c63c01b58e2',
        '63d59de0-17a3-11f1-8403-9c63c01b58e2',
        '63d5e4ea-17a3-11f1-8403-9c63c01b58e2',
        '63d626bc-17a3-11f1-8403-9c63c01b58e2',
        '63d67789-17a3-11f1-8403-9c63c01b58e2',
        'd3bed0c2-fa8d-11f0-8b0a-9c63c01b58e2'
    ];

    useEffect(() => {
        if (activeTab === 'darling') {
            fetchData(darlingBranchIds);
        } else if (activeTab === 'tvc') {
            fetchData(tvcBranchIds);
        }
    }, [activeTab]);

    const fetchData = async (ids) => {
        try {
            setLoading(true);
            const results = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const [clientRes, summaryRes] = await Promise.all([
                            clientsAPI.get(id),
                            salesAPI.getCustomerSales(id)
                        ]);

                        const transactions = summaryRes.data.sales || [];
                        const totalSalesAmount = transactions
                            .filter(tx => ['sale', 'cash', 'cheque', 'bank_transfer', 'upi'].includes((tx.transaction_type || '').toLowerCase()))
                            .reduce((sum, tx) => sum + (parseFloat(tx.total_amount || tx.amount || 0)), 0);

                        const salesBalance = transactions
                            .filter(tx => (tx.transaction_type || '').toLowerCase() !== 'opening')
                            .reduce((sum, tx) => {
                                const type = (tx.transaction_type || '').toLowerCase();
                                const total = parseFloat(tx.total_amount || tx.amount || 0);
                                const paid = parseFloat(tx.amount_paid || 0);

                                if (['sale', 'cash', 'cheque', 'bank_transfer', 'upi'].includes(type)) {
                                    return sum + (total - paid);
                                } else if (type === 'payment') {
                                    return sum - paid;
                                } else if (type === 'credit_note') {
                                    return sum - total;
                                }
                                return sum;
                            }, 0);

                        return {
                            id,
                            name: clientRes.data.customer?.name || clientRes.data.name || 'Unknown Branch',
                            totalSales: totalSalesAmount,
                            balance: salesBalance
                        };
                    } catch (err) {
                        return { id, name: `Branch ${id.substring(0, 8)}`, totalSales: 0, balance: 0, error: true };
                    }
                })
            );
            setBranchData(results);
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const totalStats = branchData.reduce((acc, curr) => ({
        sales: acc.sales + curr.totalSales,
        balance: acc.balance + curr.balance
    }), { sales: 0, balance: 0 });

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-1">Major Client Report</h1>
                    <p className="text-muted">Consolidated reports for key clients and their branches</p>
                </div>
                <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left me-2"></i>Back
                </button>
            </div>

            <div className="payment-tabs mb-4">
                <button
                    className={`tab-btn ${activeTab === 'darling' ? 'active' : ''}`}
                    onClick={() => setActiveTab('darling')}
                >
                    Darling TVC
                </button>
                <button
                    className={`tab-btn ${activeTab === 'tvc' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tvc')}
                >
                    TVC
                </button>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-6 mb-3 mb-md-0">
                    <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                        <div className="card-body p-4 position-relative overflow-hidden">
                            <h6 className="card-subtitle mb-2 opacity-75 fw-bold">Consolidated Total Sales</h6>
                            <h2 className="card-title mb-0 fw-bold">₹ {totalStats.sales.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                            <i className="bi bi-cart-check position-absolute top-50 end-0 translate-middle-y me-3 opacity-25" style={{ fontSize: '4rem' }}></i>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)', color: 'white' }}>
                        <div className="card-body p-4 position-relative overflow-hidden">
                            <h6 className="card-subtitle mb-2 opacity-75 fw-bold">Total Sales Balance</h6>
                            <h2 className="card-title mb-0 fw-bold">₹ {totalStats.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                            <i className="bi bi-cash-stack position-absolute top-50 end-0 translate-middle-y me-3 opacity-25" style={{ fontSize: '4rem' }}></i>
                        </div>
                    </div>
                </div>
            </div>

            {/* Branch Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="mb-0">{activeTab === 'darling' ? 'Darling TVC' : 'TVC'} - Branch Details</h5>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="p-5 text-center"><div className="spinner-border"></div></div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4">Branch Name</th>
                                        <th className="text-end">Total Sales (₹)</th>
                                        <th className="text-end pe-4">Sales Balance (₹)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {branchData.map(branch => (
                                        <tr key={branch.id} onClick={() => navigate(`/customer-sales/${branch.id}`)} style={{ cursor: 'pointer' }}>
                                            <td className="ps-4 fw-bold">{branch.name}</td>
                                            <td className="text-end">₹ {branch.totalSales.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                            <td className="text-end pe-4">
                                                <span className={`badge ${branch.balance > 0 ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'} rounded-pill px-3`}>
                                                    ₹ {branch.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light fw-bold">
                                    <tr>
                                        <td className="ps-4">TOTAL</td>
                                        <td className="text-end">₹ {totalStats.sales.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        <td className="text-end pe-4">₹ {totalStats.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MajorClientReport;
